import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import "highcharts/highcharts-more";
import "highcharts/highcharts-3d";
import HighchartsReact from "highcharts-react-official";
import { CHART_COLORS } from "../config/chartColors";
import { formatChartDecimal } from "../util/chartFormat";

function drawdownAxisLabelStep(categoryCount) {
  if (categoryCount > 120) return 12;
  if (categoryCount > 60) return 6;
  if (categoryCount > 24) return 3;
  return 1;
}

const ATTRIBUTION_SHORT_LABELS = {
  "% of Total Assets": "Assets",
  "% of Total Risk": "Risk",
  "% of Total Return": "Return",
};

function applyRiskAttributionChartPresentation(out, isMobile) {
  out.chart = {
    ...(out.chart || {}),
    marginBottom: isMobile ? 170 : 145,
    spacingBottom: 12,
  };
  out.legend = {
    ...(out.legend || {}),
    enabled: true,
    layout: isMobile ? "vertical" : "horizontal",
    align: "center",
    verticalAlign: "bottom",
    y: -10,
    maxHeight: isMobile ? 110 : 90,
    navigation: { enabled: true },
    itemStyle: {
      ...(out.legend?.itemStyle || {}),
      fontSize: isMobile ? "10px" : "11px",
    },
  };
  if (out.xAxis) {
    const categories = out.xAxis.categories || [];
    out.xAxis = {
      ...out.xAxis,
      offset: 8,
      labels: {
        ...(out.xAxis.labels || {}),
        rotation: 0,
        style: {
          ...(out.xAxis.labels?.style || {}),
          color: "#374151",
          fontSize: isMobile ? "10px" : "11px",
        },
        formatter: function attributionAxisLabelFormatter() {
          const full = categories[this.pos] ?? this.value;
          if (ATTRIBUTION_SHORT_LABELS[full]) {
            return ATTRIBUTION_SHORT_LABELS[full];
          }
          return full;
        },
      },
    };
  }
  if (out.plotOptions?.column) {
    out.plotOptions = {
      ...out.plotOptions,
      column: {
        ...out.plotOptions.column,
        pointPadding: isMobile ? 0.05 : 0.08,
        groupPadding: isMobile ? 0.08 : 0.12,
      },
    };
  }
  return out;
}

/** Evita que extraOptions.xAxis (ej. solo labels.rotation) sustituya todo el eje y borre categories. */
function mergeCartesianAxis(base, overlay) {
  if (overlay == null || overlay === undefined) {
    return base;
  }
  if (base == null || base === undefined) {
    return overlay;
  }
  const out = {
    ...base,
    ...overlay,
    categories:
      overlay.categories !== undefined ? overlay.categories : base.categories,
    labels: {
      ...(typeof base.labels === "object" && base.labels ? base.labels : {}),
      ...(typeof overlay.labels === "object" && overlay.labels
        ? overlay.labels
        : {}),
    },
  };
  if (
    typeof base.title === "object" &&
    base.title &&
    typeof overlay.title === "object" &&
    overlay.title
  ) {
    out.title = { ...base.title, ...overlay.title };
  } else if (overlay.title !== undefined) {
    out.title = overlay.title;
  }
  if (
    typeof base.stackLabels === "object" &&
    base.stackLabels &&
    typeof overlay.stackLabels === "object" &&
    overlay.stackLabels
  ) {
    out.stackLabels = { ...base.stackLabels, ...overlay.stackLabels };
  } else if (overlay.stackLabels !== undefined) {
    out.stackLabels = overlay.stackLabels;
  }
  return out;
}

/** Con muchas categorías mensuales (drawdowns), reduce etiquetas en el eje para no saturar. */
function mergeChartTypeOptions(chartLayout, baseChart, overlayChart) {
  const overlay = overlayChart && typeof overlayChart === "object" ? overlayChart : {};
  const base = baseChart && typeof baseChart === "object" ? baseChart : {};
  if (chartLayout === "polar") {
    return { ...base, ...overlay };
  }
  return { ...base, ...overlay, polar: false };
}

function buildCartesianTooltip(overlay = {}) {
  return {
    shared: true,
    backgroundColor: overlay.backgroundColor ?? "rgba(17,24,39,0.9)",
    style: overlay.style ?? { color: "#fff" },
    borderColor: overlay.borderColor ?? "rgba(255,255,255,0.15)",
    formatter: function cartesianTooltipFormatter() {
      const pts = this.points || (this.point ? [this.point] : []);
      const cat = pts[0]?.key ?? pts[0]?.category ?? this.x;
      let html =
        cat != null && cat !== "" ? `<span>${cat}</span><br/>` : "";
      pts.forEach((p) => {
        if (!p) return;
        html += `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${formatChartDecimal(p.y)}</b><br/>`;
      });
      return html;
    },
  };
}

function applyDrawdownChartPresentation(out) {
  out.legend = { ...(out.legend || {}), enabled: false };
  if (out.xAxis) {
    const count = out.xAxis.categories?.length ?? 0;
    const step = drawdownAxisLabelStep(count);
    out.xAxis = {
      ...out.xAxis,
      labels: {
        ...(out.xAxis.labels || {}),
        enabled: true,
        step,
        rotation: count > 24 ? -45 : 0,
        style: {
          ...(out.xAxis.labels?.style || {}),
          color: "#374151",
          fontSize: count > 120 ? "10px" : "11px",
        },
      },
    };
  }
  return out;
}

function applyCartesianDecimalFormatting(out) {
  out.tooltip = buildCartesianTooltip(out.tooltip);
  if (out.yAxis?.stackLabels?.enabled) {
    out.yAxis = {
      ...out.yAxis,
      stackLabels: {
        ...out.yAxis.stackLabels,
        formatter: function stackLabelFormatter() {
          return formatChartDecimal(this.total);
        },
      },
    };
  }
  return out;
}

export default function HighchartWidget({
  type = "pie",
  chartLayout = "cartesian",
  categories = [],
  series = [],
  options = {},
  height = "auto",
  title,
  subtitle,
  hideCategoryAxisLabels = false,
  riskAttributionPresentation = false,
}) {
  // --- estado/orientación + tamaño contenedor ---
  const wrapRef = useRef(null);
  const chartRef = useRef(null);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(orientation: landscape)").matches
      : false
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 420 : false
  );
  const [containerHeight, setContainerHeight] = useState(320);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 420);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const mm = window.matchMedia("(orientation: landscape)");
    const onChange = () => setIsLandscape(mm.matches);
    onChange();
    mm.addEventListener?.("change", onChange);
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    return () => {
      mm.removeEventListener?.("change", onChange);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);

  const readCssVarPx = (name, fallback) => {
    try {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
      if (!v) return fallback;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    } catch {
      return fallback;
    }
  };

  // Calcula altura óptima según ancho disponible y orientación
  useEffect(() => {
    if (!wrapRef.current) return;

    const ro = new ResizeObserver(() => {
      const w = wrapRef.current?.clientWidth || 320;

      // Altura disponible real (restamos header/bottombar si existen)
      const headerH = readCssVarPx("--header-h", 56);
      const bottomH = isLandscape ? 0 : readCssVarPx("--bottombar-h", 96);
      const availH = Math.max(200, window.innerHeight - headerH - bottomH - 24);

      // Heurística agradable para pie:
      // - portrait: casi cuadrado y grande (ratio ~1)
      // - landscape: un poco más bajo (ratio ~0.65)
      let targetH;
      if (type === "pie") {
        const ratio = isLandscape ? 0.65 : 1.0;
        targetH = Math.round(Math.min(availH, w * ratio));

        // límites suaves para que nunca se vea micro ni gigantesco
        const minH = isLandscape ? 220 : 260;
        const maxH = isLandscape ? 420 : 560;
        targetH = Math.max(minH, Math.min(maxH, targetH));
      } else if (riskAttributionPresentation && !isLandscape) {
        targetH = Math.round(Math.min(availH, w * 0.92));
        targetH = Math.max(340, Math.min(560, targetH));
      } else if (chartLayout === "polar") {
        const ratio = isLandscape ? 0.55 : 0.72;
        targetH = Math.round(Math.min(availH, w * ratio));
        targetH = Math.max(260, Math.min(520, targetH));
      } else {
        // para otras series (líneas/barras) un 60% del ancho
        targetH = Math.round(Math.min(availH, w * 0.6));
        targetH = Math.max(240, Math.min(520, targetH));
      }

      setContainerHeight(targetH);
    });

    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [isLandscape, type, chartLayout, riskAttributionPresentation]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart && containerHeight) {
      chart.setSize(null, containerHeight, false);
      chart.reflow();
    }
  }, [containerHeight, chartLayout, type]);

  // --- opciones base (dependen de containerHeight) ---
  const baseOptions = useMemo(
    () => ({
      chart: {
        type,
        backgroundColor: "transparent",
        height: containerHeight, // 👈 altura dinámica real
        animation: true,
      },
      title: {
        text: title || undefined,
        style: { color: "#111827", fontWeight: "700" },
      },
      subtitle: {
        text: subtitle || undefined,
        style: { color: "#6b7280" },
      },
      xAxis: categories?.length
        ? {
            categories,
            lineColor: "rgba(0,0,0,0.1)",
            tickColor: "rgba(0,0,0,0.1)",
            labels: { style: { color: "#374151" } },
          }
        : undefined,
      yAxis:
        type !== "pie"
          ? {
              title: { text: null },
              gridLineColor: "rgba(0,0,0,0.08)",
              labels: { style: { color: "#374151" } },
            }
          : undefined,
      legend: {
        enabled: true,
        itemStyle: { color: "#111827", fontWeight: "600" },
      },
      tooltip: {
        shared: type !== "pie",
        valueDecimals: 2,
        backgroundColor: "rgba(17,24,39,0.9)",
        style: { color: "#fff" },
        borderColor: "rgba(255,255,255,0.15)",
      },
      plotOptions: {
        series: {
          animation: { duration: 300 },
          dataLabels: { enabled: false },
        },
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            // En phones el label sólo %; en pantallas anchas nombre + %
            format:
              window.innerWidth < 500
                ? "{point.percentage:.1f} %"
                : "<b>{point.name}</b>: {point.percentage:.1f} %",
            style: { color: "#111827", fontWeight: 700 },
            distance: isLandscape ? 10 : 20,
          },
          showInLegend: true,
        },
      },
      credits: { enabled: false },
      series,
      colors: CHART_COLORS,
      responsive: {
        rules: [
          {
            condition: { maxWidth: 420 },
            chartOptions: {
              legend: { itemStyle: { fontSize: "11px" } },
              xAxis: { labels: { style: { fontSize: "10px" } } },
            },
          },
        ],
      },
    }),
    [type, categories, series, title, subtitle, containerHeight, isLandscape]
  );

  const merged = useMemo(() => {
    const xAxisMerged = mergeCartesianAxis(baseOptions.xAxis, options.xAxis);
    const yAxisMerged = mergeCartesianAxis(
      baseOptions.yAxis,
      options.yAxis
    );
    const mergedChart = mergeChartTypeOptions(
      chartLayout,
      baseOptions.chart,
      options.chart
    );
    const out = {
      ...baseOptions,
      ...options,
      chart: mergedChart,
      xAxis: xAxisMerged,
      yAxis: yAxisMerged,
      plotOptions: {
        ...baseOptions.plotOptions,
        ...(options.plotOptions || {}),
        pie: {
          ...(baseOptions.plotOptions?.pie || {}),
          ...(options.plotOptions?.pie || {}),
        },
        series: {
          ...(baseOptions.plotOptions?.series || {}),
          ...(options.plotOptions?.series || {}),
        },
      },
      colors: options.colors || baseOptions.colors,
      series: options.series || baseOptions.series,
    };
    if (chartLayout === "cartesian") {
      out.pane = undefined;
    }
    if (chartLayout === "cartesian" && type !== "pie") {
      applyCartesianDecimalFormatting(out);
      if (hideCategoryAxisLabels) {
        applyDrawdownChartPresentation(out);
      }
      if (riskAttributionPresentation) {
        applyRiskAttributionChartPresentation(out, isMobile);
      }
    }
    return out;
  }, [
    baseOptions,
    options,
    chartLayout,
    type,
    hideCategoryAxisLabels,
    riskAttributionPresentation,
    isMobile,
  ]);


  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={merged}
        ref={chartRef}
      />
    </div>
  );
}
